import { useState } from "react";

import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type DirectoryEntry, listDirectory } from "@/api/directory";
import { PageHeader } from "@/components/PageHeader";
import { PractitionerAvatar } from "@/components/PractitionerAvatar";

export function DirectoryPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");

  const dir = useQuery({
    queryKey: ["directory", q],
    queryFn: () => listDirectory({ q: q || undefined, limit: 400 }),
  });

  return (
    <Box>
      <PageHeader
        helpTerm="annuaire"
        title={t("directory.title")}
        subtitle={t("directory.subtitle")}
      />

      <TextField
        size="small"
        placeholder={t("directory.searchPlaceholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ minWidth: { xs: "100%", sm: 360 }, mb: 2 }}
      />

      {dir.isLoading && <CircularProgress />}
      {dir.isError && (
        <Alert severity="error">{(dir.error as Error).message}</Alert>
      )}
      {dir.data && dir.data.total === 0 && (
        <Alert severity="info">{t("directory.empty")}</Alert>
      )}

      {dir.data && dir.data.total > 0 && (
        <Grid container spacing={2}>
          {dir.data.items.map((e: DirectoryEntry) => (
            <Grid key={e.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <PractitionerAvatar
                      practitionerId={e.id}
                      hasPhoto={e.has_photo}
                      displayName={e.display_name}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {e.display_name}
                      </Typography>
                      {e.rpps && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          RPPS {e.rpps}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack spacing={0.75}>
                    {e.phone && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={t("directory.call")}>
                          <IconButton
                            size="small"
                            component="a"
                            href={`tel:${e.phone}`}
                            color="primary"
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="body2"
                          component="a"
                          href={`tel:${e.phone}`}
                          sx={{ color: "primary.main", textDecoration: "none", fontFamily: "monospace" }}
                        >
                          {e.phone}
                        </Typography>
                      </Stack>
                    )}
                    {e.email && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={t("directory.email")}>
                          <IconButton
                            size="small"
                            component="a"
                            href={`mailto:${e.email}`}
                            color="primary"
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="body2"
                          component="a"
                          href={`mailto:${e.email}`}
                          sx={{
                            color: "primary.main",
                            textDecoration: "none",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.email}
                        </Typography>
                      </Stack>
                    )}
                    {e.bio && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          mt: 0.5,
                        }}
                      >
                        {e.bio}
                      </Typography>
                    )}
                    {e.languages.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {e.languages.map((l) => (
                          <Chip
                            key={l}
                            size="small"
                            variant="outlined"
                            label={l.toUpperCase()}
                            sx={{ fontFamily: "monospace", fontSize: 11 }}
                          />
                        ))}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {e.organizations.map((o) => (
                        <Tooltip key={o.id} title={o.name}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${o.code} · ${o.membership_kind}`}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
